// ============================================================================
// PHONE ORDER PERMISSIONS — PR0 (ortak guard yardımcısı)
// ============================================================================
//
// Telefon siparişlerinde "İptal Et" (PR1) ve "Sepeti Düzenle" (PR2)
// aksiyonlarının görünürlük + disable kurallarını TEK YERDE belirler.
//
// Tasarım:
//  - Sıralı kontrol: ilk başarısız koşul `{ allowed: false, reason }` döner.
//  - Hepsi geçerse `{ allowed: true }` döner.
//  - Yeni alan/yeni guard eklenecekse: önce `PermissionReason` union'a literal ekle,
//    sonra `REASON_I18N_KEY` map'ine de eklemeyi unutma (compile-time exhaustive olur).
//
// NOT: PackageOrder interface'inde `courierId` ALANI YOK — `courierName` doluluğu
// kurye atanma sinyali olarak kullanılıyor (bk. types/index.ts satır 33).
// ============================================================================

import type { PackageOrder } from '../types';

/**
 * Aksiyonun neden bloke olduğunu açıklayan kategorik literal.
 * Tooltip / Snackbar metni `REASON_I18N_KEY` üzerinden üretilir.
 */
export type PermissionReason =
  | 'not_phone_source'   // Sipariş kaynağı 'phone' değil
  | 'pavo_active'        // Pavo'ya gönderilmiş aktif (pending/processing) ödeme var
  | 'pavo_paid'          // Pavo ödemesi tamamlanmış
  | 'is_paid'            // Manuel/kasa ödemesi alınmış (order.isPaid === true)
  | 'status_terminal'    // Sipariş terminal durumda (delivered/cancelled)
  | 'courier_assigned';  // Kurye atanmış — sepet kilitli (sadece edit guard'ı için)

export interface PermissionResult {
  allowed: boolean;
  reason?: PermissionReason;
}

/**
 * Guard çağrılırken UI tarafının sağlaması gereken context.
 * Pavo kontrolleri PavoPaymentRequest tablosuna lookup gerektirdiği için
 * helper'ın kendisine geçirilir; helper saf (pure) kalır.
 */
export interface PhoneOrderPermissionContext {
  hasActivePavoPayment: boolean;
  isPavoPaid: boolean;
}

/**
 * İptal & düzenleme için ortak ön-guard zinciri.
 * İlk başarısız koşul reason ile döner.
 */
function checkBaseGuards(
  order: PackageOrder,
  ctx: PhoneOrderPermissionContext,
): PermissionResult {
  if (order.source !== 'phone') {
    return { allowed: false, reason: 'not_phone_source' };
  }
  if (ctx.hasActivePavoPayment) {
    return { allowed: false, reason: 'pavo_active' };
  }
  if (ctx.isPavoPaid) {
    return { allowed: false, reason: 'pavo_paid' };
  }
  if (order.isPaid === true) {
    return { allowed: false, reason: 'is_paid' };
  }
  if (order.status === 'delivered' || order.status === 'cancelled') {
    return { allowed: false, reason: 'status_terminal' };
  }
  return { allowed: true };
}

/**
 * Telefon siparişi iptal edilebilir mi?
 *
 * Kurallar (sıralı):
 *  1. order.source === 'phone'
 *  2. !ctx.hasActivePavoPayment
 *  3. !ctx.isPavoPaid
 *  4. !order.isPaid
 *  5. order.status ∉ {delivered, cancelled}
 *
 * Kurye atanma kontrolü iptal için ŞART DEĞİL — yalnızca `canEditPhoneOrderItems`
 * için geçerlidir (kurye taşırken bile sipariş iptal edilebilir, çağrılan tarafa
 * kurye'yi de bilgilendirme sorumluluğu kalır).
 */
export function canCancelPhoneOrder(
  order: PackageOrder,
  ctx: PhoneOrderPermissionContext,
): PermissionResult {
  return checkBaseGuards(order, ctx);
}

/**
 * Telefon siparişinin sepet kalemleri (ürün ekle/sil/adet/not) düzenlenebilir mi?
 *
 * `canCancelPhoneOrder`'ın tüm kuralları + ek olarak:
 *  6. order.courierName boş (kurye atanmamış)
 */
export function canEditPhoneOrderItems(
  order: PackageOrder,
  ctx: PhoneOrderPermissionContext,
): PermissionResult {
  const base = checkBaseGuards(order, ctx);
  if (!base.allowed) return base;
  if (order.courierName && order.courierName.trim().length > 0) {
    return { allowed: false, reason: 'courier_assigned' };
  }
  return { allowed: true };
}

/**
 * `PermissionReason` → i18n key map'i.
 *
 * PR1 (cancel) ve PR2 (edit) bu map'i import edip tooltip / Snackbar metnini
 * `t(REASON_I18N_KEY[reason])` şeklinde üretir. Karşılık gelen i18n stringler
 * PR1'de `gts-web/src/i18n/locales/{tr,en}/orderManagement.json` altına eklenir
 * (`permissions.*` block).
 *
 * Record<PermissionReason, string> compile-time exhaustive olduğu için, yeni bir
 * reason eklendiğinde TS otomatik hata verir → tooltip metni eklemeyi unutmazsın.
 */
export const REASON_I18N_KEY: Record<PermissionReason, string> = {
  not_phone_source: 'orderManagement.permissions.notPhoneSource',
  pavo_active: 'orderManagement.permissions.pavoActive',
  pavo_paid: 'orderManagement.permissions.pavoPaid',
  is_paid: 'orderManagement.permissions.isPaid',
  status_terminal: 'orderManagement.permissions.statusTerminal',
  courier_assigned: 'orderManagement.permissions.courierAssigned',
};
